import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';

const BASE_URL = process.env.NEXT_PUBLIC_INVENTORY_API_URL ?? 'http://localhost:8082';

export interface ApiResponseBase<T = unknown> {
  success: boolean;
  message: string;
  data: T;
}

export type CategoryItem = { id: number; name: string; is_active?: boolean };

export const categoriesApi = createApi({
  reducerPath: 'categoriesApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['Categories'],
  endpoints: (builder) => ({
    // GET /v1/categories?query=&limit=
    getCategories: builder.query<
      ApiResponseBase<{ items: CategoryItem[] }>,
      { query?: string; limit?: number } | void
    >({
      query: (args) => {
        const hasQ = !!args?.query;
        const lp = args?.limit ? `limit=${args.limit}` : '';
        const qp = hasQ ? `query=${encodeURIComponent(args!.query!)}` : '';
        const join = lp && qp ? `?${qp}&${lp}` : lp ? `?${lp}` : qp ? `?${qp}` : '';
        return { url: `/v1/categories${join}`, method: 'GET' } as const;
      },
      transformResponse: (resp: any): ApiResponseBase<{ items: CategoryItem[] }> => {
        const raw = resp?.data?.items ?? [];
        const items: CategoryItem[] = Array.isArray(raw)
          ? raw.map((it: any) => ({
              id: Number(it?.id ?? it?.ID ?? 0),
              name: String(it?.name ?? it?.Name ?? ''),
              is_active: it?.is_active != null ? Boolean(it.is_active) : undefined,
            }))
          : [];
        return { success: !!resp?.success, message: String(resp?.message ?? ''), data: { items } };
      },
      transformErrorResponse: handlerErrorResponse('Categories.List'),
      providesTags: ['Categories'],
    }),

    // POST /v1/categories
    createCategory: builder.mutation<ApiResponseBase<CategoryItem>, { name: string }>({
      query: (body) => ({ url: '/v1/categories', method: 'POST', body }),
      transformResponse: (resp: any): ApiResponseBase<CategoryItem> => {
        const data = {
          id: Number(resp?.data?.id ?? resp?.data?.ID ?? 0),
          name: String(resp?.data?.name ?? resp?.data?.Name ?? ''),
          is_active: resp?.data?.is_active != null ? Boolean(resp.data.is_active) : undefined,
        };
        return { success: !!resp?.success, message: String(resp?.message ?? ''), data };
      },
      transformErrorResponse: handlerErrorResponse('Categories.Create'),
      invalidatesTags: ['Categories'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al crear categoría',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Categories.Create'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'categories.create',
              endpointName: e?.endpointName || 'Categories.Create',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PUT /v1/categories/{id}/active
    toggleCategoryActive: builder.mutation<
      ApiResponseBase<{ id: number; is_active: boolean }>,
      { id: number | string; is_active: boolean }
    >({
      query: ({ id, is_active }) => ({ url: `/v1/categories/${id}/active`, method: 'PUT', body: { is_active } }),
      transformResponse: (resp: any): ApiResponseBase<{ id: number; is_active: boolean }> => ({
        success: !!resp?.success,
        message: String(resp?.message ?? ''),
        data: {
          id: Number(resp?.data?.id ?? resp?.data?.ID ?? resp?.id ?? 0),
          is_active: resp?.data?.is_active != null ? Boolean(resp.data.is_active) : Boolean(resp?.is_active),
        },
      }),
      transformErrorResponse: handlerErrorResponse('Categories.ToggleActive'),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Categories', id: String(arg.id) }, 'Categories'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar categoría',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Categories.ToggleActive'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'categories.toggle',
              endpointName: e?.endpointName || 'Categories.ToggleActive',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
  }),
});

export const { useGetCategoriesQuery, useLazyGetCategoriesQuery, useCreateCategoryMutation, useToggleCategoryActiveMutation } = categoriesApi;
